import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/useUserRole';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { triggerIntegrationSync, SOURCE_TO_INTEGRATION_TYPE } from '@/lib/integrationSync';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, History, FileCheck, Gavel, FileText, Plug, RotateCcw } from 'lucide-react';

interface TimelineEvent {
  event_id: string;
  source: 'ai_output_review' | 'decision_audit' | 'ai_output_audit' | 'integration_audit';
  actor_user_id: string;
  actor_email: string | null;
  action: string;
  previous_status: string | null;
  new_status: string | null;
  notes: string | null;
  entity_title: string | null;
  created_at: string;
}

const SOURCE_CONFIG: Record<TimelineEvent['source'], { label: string; icon: React.ElementType }> = {
  ai_output_review: { label: 'AI Output Review', icon: FileCheck },
  decision_audit: { label: 'Decision', icon: Gavel },
  ai_output_audit: { label: 'AI Report', icon: FileText },
  integration_audit: { label: 'Integration', icon: Plug },
};

const GovernanceTimeline = () => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<TimelineEvent | null>(null);
  const { isPMO } = useUserRole();
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase.rpc('get_governance_timeline', { p_limit: 50 });
    if (fetchError) {
      setError(fetchError.message);
    } else {
      setEvents((data ?? []) as TimelineEvent[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Active integration connections for this tenant, used to resolve a
  // deleted record's source back to the connection that can re-sync it.
  const { data: integrationConfigs } = useQuery({
    queryKey: ['integration-configs-for-restore'],
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('integration_configs')
        .select('id, integration_type');
      if (fetchError) throw fetchError;
      return data;
    },
    enabled: isPMO,
  });

  const restoreMutation = useMutation({
    mutationFn: async (event: TimelineEvent) => {
      const source = event.entity_title;
      const integrationType = source ? SOURCE_TO_INTEGRATION_TYPE[source] : undefined;
      const matches = integrationType
        ? (integrationConfigs ?? []).filter((c) => c.integration_type === integrationType)
        : [];

      if (matches.length === 0) {
        throw new Error(`No active "${source}" connection found — reconnect the integration first, then try again.`);
      }

      const results = await Promise.allSettled(
        matches.map((c) => triggerIntegrationSync(c.id, c.integration_type)),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length === results.length) {
        throw new Error('Sync failed for every matching connection.');
      }
      return { total: results.length, failed: failed.length };
    },
    onSuccess: (result) => {
      toast({
        title: 'Restore sync triggered',
        description: result.failed > 0
          ? `${result.total - result.failed}/${result.total} connection(s) synced — check the Integrations page for details.`
          : 'If the record still exists in the source system, it will reappear shortly. If it was deleted there too, nothing will come back.',
      });
    },
    onError: (err: Error) => {
      toast({ title: 'Restore failed', description: err.message, variant: 'destructive' });
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-primary" />
          <CardTitle>Governance Timeline</CardTitle>
        </div>
        <CardDescription>Detection → analysis → review → decision, in one chronological feed</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No governance events recorded yet.</p>
        ) : (
          <div className="space-y-3">
            {events.map((e) => {
              const config = SOURCE_CONFIG[e.source];
              const Icon = config.icon;
              const isRestorableDelete = e.source === 'integration_audit' && e.action === 'synced_record_deleted';
              return (
                <div key={`${e.source}-${e.event_id}`} className="flex items-start gap-3 border-b border-border/50 pb-3 last:border-0">
                  <Icon className="w-4 h-4 text-muted-foreground mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">{config.label}</Badge>
                      <span className="text-sm font-medium text-foreground capitalize">{e.action.replace(/_/g, ' ')}</span>
                      {e.entity_title && <span className="text-sm text-muted-foreground truncate">{e.entity_title}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <span>{e.actor_email ?? 'Unknown user'}</span>
                      <span>·</span>
                      <span>{new Date(e.created_at).toLocaleString()}</span>
                    </div>
                    {e.notes && <p className="text-xs text-muted-foreground mt-1">{e.notes}</p>}
                  </div>
                  {isRestorableDelete && isPMO && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 shrink-0"
                      onClick={() => setRestoreTarget(e)}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Restore
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!restoreTarget} onOpenChange={(open) => { if (!open) setRestoreTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this deleted record?</AlertDialogTitle>
            <AlertDialogDescription>
              This triggers a re-sync of "{restoreTarget?.entity_title}". If the record still exists in the source
              system, it will reappear here. If it was also deleted there, nothing will come back — this doesn't
              recreate data out of nothing, it just re-pulls whatever the source currently has.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRestoreTarget(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!restoreTarget) return;
                restoreMutation.mutate(restoreTarget);
                setRestoreTarget(null);
              }}
            >
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default GovernanceTimeline;
